import React, { useEffect, useState } from "react";
import styles from "./Audits.module.scss";
import Table from "../../components/Table/Table";
import MultiSelect from "../../components/MultiSelect/MultiSelect";
import "react-datepicker/dist/react-datepicker.css";
import Input from "../../components/Input/Input";
import classNames from "classnames";
import { showModal } from "../../redux/modalStore";
import { useDispatch, useSelector } from "react-redux";
import AuditsModal from "../../modals/AuditsModal";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import PageLoader from "../../components/Loader/PageLoader";
import { AUDITS_LOADER } from "../../constants/loader";
import { formatDate, getLinkedData, handleExport } from "../../utils/helpers";
import _ from "lodash";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { setLinkedData } from "../../redux/organizationStore";
import DateRangeFilter from "../../components/DateRangeFilter/DateFIlterRange";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import Button from "../../components/Button/Button";
import ArchiveIcon from "../../icons/archive.svg";
import ExportIcon from "../../icons/export.svg";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import Swal from "sweetalert2";
import BulkEditModal from "../../modals/BulkEditModal/BulkEditModal";
import EditIcon from "../../icons/edit.svg";
import {
  clearResetSelection,
  resetSelection,
} from "../../redux/rowSelectionStore";
import ConfirmationPopup from "../../modals/ConfirmationPopup";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import { hideOverlay, showOverlay } from "../../redux/overlayLoaderStore";
import CalendarLinkPopup from "../../modals/CalendarLinkPopup/CalendarLinkPopup";
import { ReactComponent as CalendarIcon } from "../../icons/calendar.svg";
import { ReactComponent as DownArrowIcon } from "../../icons/down-arrow.svg";
import { ReactComponent as UpArrowIcon } from "../../icons/up-arrow.svg";
import SecondaryButton from "../../components/Button/SecondaryButton";
import { updateFloatingBarVisible } from "../../redux/layoutStore";
import { setMeMode, setMeModeTriggeredByKanban } from "../../redux/filterStore";

const Audits = () => {
  const dispatch = useDispatch();
  const { orgId, auditKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionToken = localStorage.getItem("session_token");
  const [searchParams] = useSearchParams();

  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [selectedFrequencies, setSelectedFrequencies] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIssueData, setSelectedIssueData] = useState(null);
  const [selectedAudits, setSelectedAudits] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [showDoneAudits, setShowDoneAudits] = useState(() => {
    const rawStatuses = searchParams.getAll("status");
    return location.state?.fromKanban && rawStatuses.includes("Done");
  });
  const [selectedDoneAudits, setSelectedDoneAudits] = useState([]);

  const { loadingContexts } = useSelector((state) => state.loader);
  const modules = useSelector((state) => state.user.modules[orgId]);
  const { meModeActive } = useSelector((state) => state.filters);
  const currentUser = useSelector((state) => state.user.email);
  const { auditsTasksData, data } = useSelector((state) => state.organization);
  const openedModal = useSelector((state) => state.modal.openedModal);
  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  const isLoading = loadingContexts[AUDITS_LOADER];

  const baseUrl = process.env.REACT_APP_API_BASE;
  const calendarFeedLink = `${baseUrl}/feed/audit_tasks.ical?session_token=${sessionToken}&org_id=${orgId}`;

  const allSelectedAudits = [...selectedAudits, ...selectedDoneAudits];

  useFetchOrganizationData();

  // UseEffect for enabling/disabling me mode when navigating through kanban columns links
  useEffect(() => {
    if (location.state?.fromKanban) {
      dispatch(setMeMode(true));
    }

    return () => {
      if (location.state?.fromKanban) {
        dispatch(setMeMode(false));
      }
    };
  }, []);

  useEffect(() => {
    if (auditKey && auditsTasksData) {
      const matchedAudit = Object.values(auditsTasksData).find(
        (audit) => audit.key === auditKey,
      );

      if (matchedAudit) {
        const linkedData = getLinkedData(matchedAudit, data);
        dispatch(setLinkedData(linkedData));
        setSelectedIssueData(matchedAudit);
        dispatch(updateFloatingBarVisible(false));
        dispatch(
          showModal({
            name: "audits_modal",
            data: matchedAudit,
          }),
        );
      }
    }
  }, [auditKey, auditsTasksData, dispatch, data]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawStatuses = params.getAll("status");

    const statusesFromUrl = rawStatuses.every((s) => !s.includes(","))
      ? rawStatuses
      : [];

    setSelectedStatuses(statusesFromUrl);
  }, [location.search]);

  // Extracting path from URL
  const lastIndex = location.pathname.lastIndexOf("/");
  const modifiedPath = location.pathname.substring(lastIndex);

  // Find the module object corresponding to the current path
  const currentModule = modules?.find((mod) => mod.path === modifiedPath);

  const uniqueChapters = auditsTasksData
    ? _.uniqBy(
        Object.values(auditsTasksData).flatMap((issue) =>
          issue?.form?.map((form) => ({
            id: `${form?.risk?.qms_id}-${form?.risk?.qms_description}`,
            label: `${form?.risk?.qms_id} - ${form?.risk?.qms_description}`,
          })),
        ),
        "id",
      )
    : [];

  const handleChapterFilterChange = (newSelectedValues) => {
    setSelectedChapters(newSelectedValues);
  };

  const auditNames = auditsTasksData
    ? _.uniqBy(
        Object.values(auditsTasksData).map((audit) => ({
          id: audit.name,
          label: audit.name,
        })),
        "id",
      )
    : [];

  const handleNameFilterChange = (newSelectedValues) => {
    setSelectedNames(newSelectedValues);
  };

  const uniqueStatuses = auditsTasksData
    ? _.uniqBy(
        Object.values(auditsTasksData).map((issue) => ({
          id: issue.status,
          label: issue.status,
        })),
        "id",
      )
    : [];

  const handleStatusFilterChange = (newSelectedValues) => {
    setSelectedStatuses(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.delete("status");
    newSelectedValues.forEach((status) => {
      newSearchParams.append("status", status);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const uniqueResponsibles = auditsTasksData
    ? _.uniqBy(
        Object.values(auditsTasksData).flatMap((issue) =>
          Array.isArray(issue.responsible)
            ? issue.responsible.map((res) => ({
                id: res,
                label: res,
              }))
            : issue.responsible
              ? [{ id: issue.responsible, label: issue.responsible }]
              : [],
        ),
        "id",
      )
    : [];

  const handleResponsibleFilterChange = (newSelectedValues) => {
    setSelectedResponsibles(newSelectedValues);
  };

  const uniqueFrequencies = auditsTasksData
    ? _.uniqBy(
        Object.values(auditsTasksData)
          .filter((item) => item.frequency)
          .map((item) => ({
            id: item.frequency,
            label:
              item.frequency.charAt(0).toUpperCase() + item.frequency.slice(1),
          })),
        "id",
      )
    : [];

  const handleFrequencyFilterChange = (newSelectedValues) => {
    setSelectedFrequencies(newSelectedValues);
  };

  const handleSelectedRowsChange = (selectedRows) => {
    setSelectedAudits(selectedRows);
  };

  const typeOptions = [
    { id: "internal", label: "Internal" },
    { id: "external", label: "External" },
  ];

  const handleTypeFilterChange = (newSelectedValues) => {
    setSelectedTypes(newSelectedValues);
  };

  const handleExportSelectedAudits = async () => {
    setLoading(true);
    setLoadingAction("export");

    await handleExport({
      selectedItems: allSelectedAudits,
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Audit",
    });

    setLoading(false);
    setLoadingAction("");
  };

  const handleBulkArchive = async () => {
    setShowConfirmationPopup(false);

    if (!allSelectedAudits.length) return;

    const payload = allSelectedAudits.map((project) => ({
      id: project.id,
      archived: true,
    }));

    const archivedKeys = allSelectedAudits.map((project) => project.key);

    setLoading(true);
    setLoadingAction("archive");
    dispatch(showOverlay());

    try {
      await axiosInstance.patch(
        `/nocodb/audit_tasks?org_id=${orgId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      // Dispatch updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          false,
          true,
          false,
          false,
        ),
      );

      // Show success alert
      Swal.fire({
        toast: true,
        icon: "success",
        title: `Archive successful`,
        text: `Keys archived: ${archivedKeys.join(", ")}`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: `Archive failed`,
        text: `Could not archive audit tasks. Please try again.`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
      setLoadingAction("");
      dispatch(hideOverlay()); // Hide overlay

      setSelectedAudits([]);
      setSelectedDoneAudits([]);

      // Trigger row selection reset
      dispatch(resetSelection());
      setTimeout(() => dispatch(clearResetSelection()), 100); // Reset to false after a short delay
    }
  };

  const handleBulkEdit = () => {
    dispatch(
      showModal({
        name: "bulk_edit_modal",
        data: allSelectedAudits,
      }),
    );
  };

  const allFilteredAudits = auditsTasksData
    ? Object.values(auditsTasksData)
        .filter((item) => !item.archived)
        .filter((item) => {
          const {
            date,
            form,
            status,
            responsible,
            accountable,
            consulted,
            informed,
            key,
            name,
            frequency,
          } = item;

          const dueDate = new Date(date);
          const formattedStartDate = dateRange[0];
          const formattedEndDate = dateRange[1];

          const searchQueryLower = searchQuery.toLowerCase();

          const filters = [
            selectedChapters.length === 0 ||
              form.some((formItem) =>
                selectedChapters.includes(
                  `${formItem.risk.qms_id}-${formItem.risk.qms_description}`,
                ),
              ),
            selectedNames.length === 0 || selectedNames.includes(name),
            selectedStatuses.length === 0 || selectedStatuses.includes(status),
            selectedResponsibles.length === 0 ||
              (Array.isArray(responsible)
                ? responsible.some((r) => selectedResponsibles.includes(r))
                : selectedResponsibles.includes(responsible)),
            selectedFrequencies.length === 0 ||
              selectedFrequencies.includes(frequency),
            selectedTypes.length === 0 ||
              selectedTypes.includes(item.type?.toLowerCase()),
            (!formattedStartDate && !formattedEndDate) || // No date range filter applied
              (dueDate >= formattedStartDate && dueDate <= formattedEndDate),
            !searchQueryLower || // If there's no search query, ignore this filter
              key?.toLowerCase().includes(searchQueryLower) ||
              name?.toLowerCase().includes(searchQueryLower) ||
              frequency?.toLowerCase().includes(searchQueryLower) ||
              (Array.isArray(responsible)
                ? responsible.some((r) =>
                    r.toLowerCase().includes(searchQueryLower),
                  )
                : responsible?.toLowerCase().includes(searchQueryLower)) ||
              form.some((formItem) =>
                `${formItem.risk.qms_id}-${formItem.risk.qms_description}`
                  ?.toLowerCase()
                  .includes(searchQueryLower),
              ),
            !meModeActive ||
              [responsible, accountable, consulted, informed].some((role) =>
                Array.isArray(role)
                  ? role.includes(currentUser)
                  : role === currentUser,
              ),
          ];

          return filters.every(Boolean);
        })
    : [];

  const activeAudits = allFilteredAudits.filter(
    (item) => item.status !== "Done",
  );
  const doneAudits = allFilteredAudits.filter((item) => item.status === "Done");

  const handleRowClick = (row) => {
    const linkedData = getLinkedData(row.original, data);
    dispatch(setLinkedData(linkedData));
    setSelectedIssueData(row.original);

    // push the new URL
    navigate(`/${orgId}/audits/${row.original.key}`, { replace: false });

    dispatch(updateFloatingBarVisible(false));
    dispatch(
      showModal({
        name: "audits_modal",
        data: row.original,
      }),
    );
  };

  const handleExportCalendar = () => {
    setShowCalendarPopup(true);
  };

  const columns = [
    {
      accessorKey: "select",
      header: ({ table }) => (
        <div
          className={styles.customCheckboxContainer}
          onClick={(e) => {
            e.stopPropagation(); // Stop click event propagation
            table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected());
          }}
        >
          {table.getIsAllPageRowsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      size: 50,
      cell: ({ row }) => (
        <div className={styles.customCheckboxContainer}>
          {row.getIsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "key",
      header: "Audit Key",
      cell: (props) => <div>{props.getValue()}</div>,
      enableSorting: true,
      sortingFn: "alphanumeric",
    },
    {
      accessorKey: "name",
      header: "Audit Name",
      size: 250,
      cell: (props) => <div>{props.getValue()}</div>,
      enableSorting: false,
    },
    {
      accessorKey: "responsible",
      header: "Responsible",
      size: 170,
      cell: (props) => {
        return props.row.original.responsible ? (
          <div>{props.row.original.responsible}</div>
        ) : (
          <div>Not defined</div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "Status",
      minSize: 90,
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "In Review":
            statusClassName = styles.inReviewStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
      enableSorting: false,
      // sortingFn: (rowA, rowB, columnId) => {
      //   // Custom sorting function for status
      //   const statusA = rowA.getValue(columnId);
      //   const statusB = rowB.getValue(columnId);
      //
      //   // If one is "Done" and the other is not, "Done" goes to the bottom
      //   if (statusA === "Done" && statusB !== "Done") return 1;
      //   if (statusA !== "Done" && statusB === "Done") return -1;
      //
      //   // If both are the same status, maintain the original order
      //   return 0;
      // },
    },
    {
      accessorKey: "date",
      header: "Due Date",
      cell: (props) => <div>{formatDate(props.getValue())}</div>,
      sortingFn: "datetime",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
      cell: (props) => (
        <div>
          {props.row.original?.frequency?.charAt(0).toUpperCase() +
            props.row.original?.frequency?.slice(1)}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "chapter",
      header: "Chapter",
      cell: (props) => {
        const uniqueRisks = _.uniqBy(
          props.row.original.form,
          (item) => `${item.risk?.qms_id} - ${item.risk?.qms_description}`,
        );
        return (
          <div>
            {uniqueRisks.map((item, index) => (
              <div key={index}>
                {item.risk?.qms_id} - {item.risk?.qms_description}
              </div>
            ))}
          </div>
        );
      },
      size: 250,
      enableSorting: false,
    },
  ];

  return (
    <MainLayout>
      <BulkEditModal modalType="audits" orgId={orgId} />
      <div className={styles.column}>
        {isLoading && <PageLoader text="Loading, it will be ready shortly" />}
        {!isLoading && (
          <>
            <Breadcrumbs data={currentModule} />
            <div className={styles.auditsContainer}>
              <AuditsModal auditData={openedModal?.data} />
              <div className={styles.stickyActionsContainer}>
                <div className={styles.actionsWrapper}>
                  <div className={styles.rowWrapper}>
                    <div className={styles.actionsRow}>
                      <Button
                        text="Bulk Edit"
                        className={styles.button}
                        onClick={handleBulkEdit}
                        disabled={allSelectedAudits.length < 2 || loading}
                        icon={EditIcon}
                        iconPosition="right"
                      />
                      <Button
                        text={
                          loading && loadingAction === "archive"
                            ? "Archiving..."
                            : "Archive"
                        }
                        className={styles.button}
                        onClick={() => setShowConfirmationPopup(true)}
                        disabled={allSelectedAudits.length === 0 || loading}
                        icon={ArchiveIcon}
                        iconPosition="right"
                      />
                      <Button
                        text={
                          loading && loadingAction === "export"
                            ? "Exporting..."
                            : "Export as Word File"
                        }
                        className={styles.button}
                        onClick={handleExportSelectedAudits}
                        disabled={allSelectedAudits.length === 0 || loading}
                        icon={ExportIcon}
                        iconPosition="right"
                      />
                    </div>
                    <div className={styles.topRow}>
                      <Button
                        text="Calendar Feed"
                        className={styles.calendarButton}
                        onClick={handleExportCalendar}
                        disabled={loading}
                        icon={<CalendarIcon />}
                        iconPosition="right"
                      />
                    </div>
                  </div>
                  <div className={styles.rowWrapper}>
                    <div className={styles.filtersWrapper}>
                      <MultiSelect
                        label={
                          selectedNames?.length > 0
                            ? selectedNames.join(", ")
                            : "Audit"
                        }
                        options={auditNames}
                        onChangeSelectedValues={handleNameFilterChange}
                        selectedValues={selectedNames}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <MultiSelect
                        label={
                          selectedResponsibles.length > 0
                            ? selectedResponsibles.join(", ")
                            : "Responsible"
                        }
                        options={uniqueResponsibles}
                        onChangeSelectedValues={handleResponsibleFilterChange}
                        selectedValues={selectedResponsibles}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <MultiSelect
                        label={
                          selectedStatuses?.length > 0
                            ? selectedStatuses.join(", ")
                            : "Status"
                        }
                        options={uniqueStatuses}
                        onChangeSelectedValues={handleStatusFilterChange}
                        selectedValues={selectedStatuses}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <MultiSelect
                        label={
                          selectedFrequencies.length > 0
                            ? selectedFrequencies.join(", ")
                            : "Frequency"
                        }
                        options={uniqueFrequencies}
                        onChangeSelectedValues={handleFrequencyFilterChange}
                        selectedValues={selectedFrequencies}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <MultiSelect
                        label={
                          selectedChapters.length > 0
                            ? uniqueChapters
                                .filter((chapter) =>
                                  selectedChapters.includes(chapter.id),
                                )
                                .map((chapter) => chapter.label)
                                .join(", ")
                            : "Chapter"
                        }
                        options={uniqueChapters}
                        onChangeSelectedValues={handleChapterFilterChange}
                        selectedValues={selectedChapters}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <MultiSelect
                        label={
                          selectedTypes.length > 0
                            ? typeOptions
                                .filter((type) =>
                                  selectedTypes.includes(type.id),
                                )
                                .map((type) => type.label)
                                .join(", ")
                            : "Type"
                        }
                        options={typeOptions}
                        onChangeSelectedValues={handleTypeFilterChange}
                        selectedValues={selectedTypes}
                        disabled={
                          !auditsTasksData || auditsTasksData.length === 0
                        }
                        className={styles.multiselect}
                      />
                      <DateRangeFilter
                        handleDateRangeChange={setDateRange}
                        dateRange={dateRange}
                        isClearable
                        className={styles.auditDateFilter}
                      />
                    </div>
                    <Input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.input}
                      icon={SearchIcon}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.tableWrapper}>
                <Table
                  columns={columns}
                  data={activeAudits}
                  onRowClick={handleRowClick}
                  onSelectedRowsChange={handleSelectedRowsChange}
                  centered
                  rowHover
                  initialSorting={[{ id: "date", desc: false }]}
                  resetSelection={resetRowSelection}
                />
              </div>
              {!activeAudits.length && (
                <div className={styles.noData}>
                  There are no active audits to display.
                </div>
              )}
              <SecondaryButton
                text={showDoneAudits ? "Hide Done Audits" : "Show Done Audits"}
                onClick={() => setShowDoneAudits((prev) => !prev)}
                className={styles.doneAuditsButton}
                icon={showDoneAudits ? <UpArrowIcon /> : <DownArrowIcon />}
                iconPosition="right"
              />
              {showDoneAudits && (
                <div className={styles.tableWrapper}>
                  <Table
                    columns={columns}
                    data={doneAudits}
                    onRowClick={handleRowClick}
                    onSelectedRowsChange={(rows) => setSelectedDoneAudits(rows)}
                    centered
                    rowHover
                    initialSorting={[{ id: "date", desc: false }]}
                    resetSelection={resetRowSelection}
                  />
                  {doneAudits.length === 0 && (
                    <div className={styles.noData}>
                      There are no done audits to display.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          message="Are you sure you want to archive selected audit tasks?"
          illustration={ArchiveIllustration}
          confirmText="Yes, archive"
          onConfirm={handleBulkArchive}
          onCancel={() => setShowConfirmationPopup(false)}
        />
      )}
      {showCalendarPopup && (
        <CalendarLinkPopup
          link={calendarFeedLink}
          onClose={() => setShowCalendarPopup(false)}
        />
      )}
    </MainLayout>
  );
};

export default Audits;
